// Conditions glossary, Core rulings (with pins), Actions in combat.
// Content extracted verbatim from the old site (site/src/content/reference.json).
import { useMemo, useState } from "react";
import reference from "../content/reference.json";
import { useStore } from "../state/store";
import { EntryCard, EntryEditor, useCustomEntries, type LibraryEntry } from "./ReferenceLibrary";

const R = reference as any as {
  rulings: { id: string; category: string; title: string; bluf: string; details: string[]; source: string; sourceLabel: string }[];
  actions: { id: string; title: string; badge: string; text: string; rows: string[]; source: string; sourceLabel: string }[];
  weaponMasteries: { id: string; name: string; summary: string; weapons?: string[]; rules: string[]; source: string; sourceLabel: string }[];
  conditionSummaries: Record<string, string>;
  conditionRules: Record<string, string[]>;
};

function SearchBox({ value, onChange, placeholder, count }: { value: string; onChange: (v: string) => void; placeholder: string; count: number }) {
  return <div className="search-box"><span>⌕</span><input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} aria-label={placeholder} /><b>{count}</b></div>;
}

export function Conditions() {
  const [q, setQ] = useState("");
  const names = Object.keys(R.conditionSummaries).filter((n) => !q || `${n} ${R.conditionSummaries[n]} ${(R.conditionRules[n] || []).join(" ")}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <section className="panel reference">
      <header className="panel-head"><div><span className="eyebrow">2024 rules glossary</span><h1>Conditions & rules states</h1><p className="muted">{names.length} entries · expand any item for the full table-ready rule.</p></div></header>
      <SearchBox value={q} onChange={setQ} placeholder="Search conditions and effects…" count={names.length} />
      <div className="ref-list">
        {names.map((n) => (
          <details key={n} className="ref-entry">
            <summary><span className={n === "Bloodied" || n === "Poisoned" ? "mark danger" : "mark"}>{n === "Poisoned" ? "☠" : n === "Bloodied" ? "♥" : "✦"}</span><span><small>{n === "Bloodied" ? "Rules state" : "Condition"}</small><strong>{n}</strong><em>{R.conditionSummaries[n]}</em></span></summary>
            <ul>{(R.conditionRules[n] || []).map((r, i) => <li key={i}>{r}</li>)}</ul>
          </details>
        ))}
      </div>
    </section>
  );
}

export function WeaponMasteries() {
  const [q, setQ] = useState("");
  const shown = R.weaponMasteries.filter((m) => !q || `${m.name} ${m.summary} ${(m.weapons || []).join(" ")} ${m.rules.join(" ")}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <section className="panel reference">
      <header className="panel-head"><div><span className="eyebrow">2024 rules glossary</span><h1>Weapon masteries</h1><p className="muted">{R.weaponMasteries.length} entries · the eight mastery properties plus how mastery is granted · expand any item for the table-ready rule and the weapons that carry it.</p></div></header>
      <SearchBox value={q} onChange={setQ} placeholder="Search masteries, effects, or a weapon…" count={shown.length} />
      <div className="ref-list">
        {shown.map((m) => (
          <details key={m.id} className="ref-entry">
            <summary><span className="mark">⚔</span><span><small>{m.weapons ? "Mastery property" : "How it works"}</small><strong>{m.name}</strong><em>{m.summary}</em></span></summary>
            <ul>{m.rules.map((r, i) => <li key={i}>{r}</li>)}</ul>
            {m.weapons && m.weapons.length > 0 && <p className="muted"><strong>Weapons:</strong> {m.weapons.join(", ")}</p>}
            <p className="source"><a href={m.source} target="_blank" rel="noreferrer">{m.sourceLabel}</a></p>
          </details>
        ))}
      </div>
    </section>
  );
}

export function CoreRulings() {
  const { layout, update } = useStore();
  const records = (layout.coreRulingRecords || {}) as Record<string, { pinned?: boolean }>;
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const categories = useMemo(() => [...new Set(R.rulings.map((r) => r.category))], []);
  const shown = R.rulings
    .filter((r) => category === "all" || r.category === category)
    .filter((r) => !q || `${r.title} ${r.bluf} ${r.details.join(" ")} ${r.category}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => Number(Boolean(records[b.id]?.pinned)) - Number(Boolean(records[a.id]?.pinned)) || a.title.localeCompare(b.title));
  const togglePin = (id: string) => update((l) => ({ ...l, coreRulingRecords: { ...((l.coreRulingRecords as any) || {}), [id]: { ...(((l.coreRulingRecords as any) || {})[id] || {}), pinned: !records[id]?.pinned } } }));
  return (
    <section className="panel reference">
      <header className="panel-head"><div><span className="eyebrow">DM reference library · 2024 rules</span><h1>Core rulings</h1><p className="muted">{R.rulings.length} rulings · A–Z by title · pinned rulings float to the top · expand any entry for the table-ready answer and official source.</p></div>
        <div className="actions"><select value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Category"><option value="all">All categories</option>{categories.map((c) => <option key={c} value={c}>{c}</option>)}</select></div></header>
      <SearchBox value={q} onChange={setQ} placeholder="Search rulings…" count={shown.length} />
      <div className="ref-list">
        {shown.map((r) => (
          <details key={r.id} className={`ref-entry ${records[r.id]?.pinned ? "pinned" : ""}`}>
            <summary><span className="mark">⚖</span><span><small>{r.category}</small><strong>{r.title}</strong><em>{r.bluf}</em></span><button className="pin" aria-label={records[r.id]?.pinned ? "Unpin" : "Pin"} onClick={(e) => { e.preventDefault(); togglePin(r.id); }}>{records[r.id]?.pinned ? "★" : "☆"}</button></summary>
            <ul>{r.details.map((d, i) => <li key={i}>{d}</li>)}</ul>
            <p className="source"><a href={r.source} target="_blank" rel="noreferrer">{r.sourceLabel}</a></p>
          </details>
        ))}
      </div>
    </section>
  );
}

export function ActionsInCombat() {
  const { entries, save } = useCustomEntries("Actions in combat");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<LibraryEntry | "new" | null>(null);
  const shown = R.actions.filter((a) => !q || `${a.title} ${a.badge} ${a.text} ${a.rows.join(" ")}`.toLowerCase().includes(q.toLowerCase()));
  const custom = entries.filter((e) => !q || `${e.title} ${e.text} ${(e.rows || []).join(" ")}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <section className="panel reference">
      <header className="panel-head"><div><span className="eyebrow">2024 SRD quick reference</span><h1>Actions & turn timing</h1><p className="muted">{R.actions.length} table-ready references · official actions, timing, and combat procedures{entries.length ? ` · ${entries.length} custom` : ""}.</p></div>
        <div className="actions"><button className="primary" onClick={() => setEditing("new")}>＋ Add entry</button></div></header>
      <SearchBox value={q} onChange={setQ} placeholder="Search actions…" count={shown.length + custom.length} />
      {editing && <EntryEditor initial={editing === "new" ? undefined : editing} onCancel={() => setEditing(null)} onSave={(e) => { save(editing === "new" ? [e, ...entries] : entries.map((x) => (x.id === e.id ? e : x))); setEditing(null); }} />}
      {custom.length > 0 && <div className="ref-grid">{custom.map((e) => <EntryCard key={e.id} entry={{ ...e, custom: true }} onEdit={() => setEditing(e)} onRemove={() => save(entries.filter((x) => x.id !== e.id))} />)}</div>}
      <div className="ref-list">
        {shown.map((a) => (
          <details key={a.id} className="ref-entry">
            <summary><span className="mark">◷</span><span><small>{a.badge}</small><strong>{a.title}</strong><em>{a.text}</em></span></summary>
            <ul>{a.rows.map((r, i) => <li key={i}>{r}</li>)}</ul>
            <p className="source"><a href={a.source} target="_blank" rel="noreferrer">{a.sourceLabel}</a></p>
          </details>
        ))}
      </div>
    </section>
  );
}
